import React, { useEffect, useState } from "react";

import { ReactComponent as ArrowDown } from "assets/arrow-down.svg";
import { ReactComponent as YellowStar } from "assets/yellow-star.svg";
import DotElastic from "components/DotElastic";
import ProjectItem from "components/ProjectItem";
import WorkspaceItem from "components/WorkspaceItem";
import { ProjectDetail } from "models/Project";
import { Workspace } from "models/Workspace";
import { getStarredProjects } from "services/ProjectService";
import { getWorkspace, getWorkspaceProjects } from "services/WorkspaceService";

import ProjectSortingOptionsDropdown, {
  ProjectSortingOptions,
} from "./ProjectSortingOptionsDropdown";
import styles from "./WorkspacesPage.module.scss";
import { arrayToObjectById, getUser } from "../../utils/helper";

const WorkspacesPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [starredProjects, setStarredProjects] = useState<ProjectDetail[]>([]);
  const [workspaces, setWorkspaces] = useState<{ [id: number]: Workspace }>({});
  const [myWorkspace, setMyWorkspace] = useState<Workspace>();

  const [myWorkspaceProjectOrder, setMyWorkspaceProjectOrder] =
    useState<ProjectSortingOptions>(ProjectSortingOptions.RECENT_ACTIVITY);
  const [sharedWorkspacesProjectsOrder, setSharedWorkspacesProjectsOrder] =
    useState<ProjectSortingOptions>(ProjectSortingOptions.RECENT_ACTIVITY);

  const [workspaceProjects, setWorkspaceProjects] = useState<{
    [id: number]: ProjectDetail[];
  }>({});
  const [nextPage, setNextPage] = useState<{ [id: number]: number | null }>({});

  const getProjectsInOneRow = () => {
    const windowWidth = window.innerWidth - 88; // Window width - (padding + margin).
    const visibleProjects = windowWidth / 324; // dividing by (project width + grid gap).
    return Math.floor(visibleProjects);
  };

  const setProjects = (workspaceId, res, reset) => {
    setWorkspaceProjects((prev) => ({
      ...prev,
      [workspaceId]: reset
        ? res.results
        : [...(prev[workspaceId] || []), ...res.results],
    }));
    const page = new URLSearchParams(res.next).get("page");
    setNextPage((prev) => ({
      ...prev,
      [workspaceId]: page ? Number(page) : null,
    }));
  };

  // Fetching user workspaces
  useEffect(() => {
    setIsLoading(true);
    getWorkspace().then((res) => {
      const user = getUser();
      setMyWorkspace(res.find((w) => w.creator.id === user.id));
      setWorkspaces(
        arrayToObjectById(res.filter((w) => w.creator.id !== user.id)),
      );
    });
    getStarredProjects(1, getProjectsInOneRow()).then((res) => {
      setStarredProjects(res.results);
      setNextPage((prev) => ({
        ...prev,
        [-1]: res.next ? parseInt(res.next.split("=").pop() || "0", 10) : null,
      }));
    });
  }, []);

  // Fetching myWorkspace Projects
  useEffect(() => {
    if (myWorkspace) {
      getWorkspaceProjects(
        myWorkspace?.id,
        1,
        getProjectsInOneRow(),
        myWorkspaceProjectOrder,
      ).then((res) => {
        setProjects(myWorkspace.id, res, true);
        if (Object.keys(workspaces).length === 0) {
          setIsLoading(false);
        }
      });
    }
  }, [myWorkspace, myWorkspaceProjectOrder, workspaces]);

  // Fetching shared workspaces Projects
  useEffect(() => {
    const workspaceIds: number[] = Object.keys(workspaces).map(Number);
    workspaceIds.forEach((id, iter) => {
      getWorkspaceProjects(
        id,
        1,
        getProjectsInOneRow(),
        sharedWorkspacesProjectsOrder,
      ).then((res) => {
        setProjects(id, res, true);
        if (iter === workspaceIds.length - 1) setIsLoading(false);
      });
    });
  }, [workspaces, sharedWorkspacesProjectsOrder]);

  const fetchMoreProjects = (id: number) => {
    if (!nextPage[id]) {
      return;
    }
    if (id === -1) {
      getStarredProjects(nextPage[id], getProjectsInOneRow()).then((res) => {
        setStarredProjects((prev) => [...prev, ...res.results]);
        setNextPage((prev) => ({
          ...prev,
          [-1]: res.next
            ? parseInt(res.next.split("=").pop() || "0", 10)
            : null,
        }));
      });
    } else {
      getWorkspaceProjects(
        id,
        nextPage[id],
        getProjectsInOneRow(),
        myWorkspace && id === myWorkspace.id
          ? myWorkspaceProjectOrder
          : sharedWorkspacesProjectsOrder,
      ).then((res) => setProjects(id, res, false));
    }
  };

  return (
    <div>
      {isLoading && (
        <div className={styles.loaderContainer}>
          <h4>Loading workspaces...</h4>
          <DotElastic />
        </div>
      )}
      {!isLoading && (
        <div className={styles.contentContainer}>
          <div className={styles.header}>
            <h3 className={styles.title}>Workspaces</h3>
          </div>
          <div className={styles.innerContainer}>
            {starredProjects.length > 0 && (
              <div className={styles.starredProjects}>
                <div className={styles.starredProjectsHeader}>
                  <div className={styles.iconContainer}>
                    <YellowStar />
                  </div>
                  <h3>Starred Projects</h3>
                </div>
                <div className={styles.projectsContainer}>
                  {starredProjects.map((project) => {
                    const workspace =
                      myWorkspace && myWorkspace.id === project.company_id
                        ? myWorkspace
                        : workspaces[project.company_id];
                    return (
                      workspace && (
                        <ProjectItem
                          key={project.id}
                          project={project}
                          starredProjects={starredProjects}
                          setStarredProjects={setStarredProjects}
                          workspace={workspace}
                          updateProject={(prj) => {
                            setWorkspaceProjects((prev) => ({
                              ...prev,
                              [workspace.id]: prj
                                ? workspaceProjects[workspace.id].map((p) =>
                                    p.id === prj.id ? prj : p,
                                  )
                                : workspaceProjects[workspace.id].filter(
                                    (p) => p.id !== project.id,
                                  ),
                            }));
                          }}
                        />
                      )
                    );
                  })}
                </div>
                {nextPage[-1] && (
                  <div className={styles.loadMoreContainer}>
                    <button
                      type="button"
                      className={styles.loadMoreButton}
                      onClick={() => fetchMoreProjects(-1)}
                    >
                      Load more projects
                      <ArrowDown />
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className={styles.myWorkspace}>
              <div className={styles.heading}>
                <h3 className={styles.title}>My Workspace</h3>
                <ProjectSortingOptionsDropdown
                  projectsOrder={myWorkspaceProjectOrder}
                  setProjectsOrder={setMyWorkspaceProjectOrder}
                />
              </div>
              {myWorkspace && (
                <WorkspaceItem
                  key={myWorkspace?.id}
                  workspace={myWorkspace}
                  updateWorkspace={(w: Workspace) => setMyWorkspace(w)}
                  projects={workspaceProjects[myWorkspace.id]}
                  starredProjects={starredProjects}
                  setStarredProjects={setStarredProjects}
                  updateWorkspaceProjects={(projects) => {
                    setWorkspaceProjects((prev) => ({
                      ...prev,
                      [myWorkspace.id]: projects,
                    }));
                  }}
                  hasNextPage={!!nextPage[myWorkspace.id]}
                  fetchMoreProjects={fetchMoreProjects}
                />
              )}
            </div>
            {Object.keys(workspaces).length > 0 && (
              <div className={styles.sharedWorkspaces}>
                <div className={styles.heading}>
                  <h3 className={styles.title}>Shared Workspaces</h3>
                  <ProjectSortingOptionsDropdown
                    projectsOrder={sharedWorkspacesProjectsOrder}
                    setProjectsOrder={setSharedWorkspacesProjectsOrder}
                  />
                </div>
                <div>
                  {Object.keys(workspaces).map((key: string) => {
                    const workspace = workspaces[key];
                    return (
                      <WorkspaceItem
                        key={workspace.id}
                        workspace={workspace}
                        updateWorkspace={(w: Workspace) =>
                          setWorkspaces({ ...workspaces, [w.id]: w })
                        }
                        projects={workspaceProjects[workspace.id]}
                        starredProjects={starredProjects}
                        setStarredProjects={setStarredProjects}
                        updateWorkspaceProjects={(projects) => {
                          setWorkspaceProjects((prev) => ({
                            ...prev,
                            [workspace.id]: projects,
                          }));
                        }}
                        hasNextPage={!!nextPage[workspace.id]}
                        fetchMoreProjects={fetchMoreProjects}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspacesPage;
